import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PriceBoard } from "@/components/commons/PriceBoard";
import { LotsMarket } from "@/components/commons/LotsMarket";
import { CoopsPanel } from "@/components/commons/CoopsPanel";
import { SchedulesPanel } from "@/components/commons/SchedulesPanel";
import { AuditPanel } from "@/components/commons/AuditPanel";
import { PolicyPanel } from "@/components/commons/PolicyPanel";
import { TreasuryPanel } from "@/components/commons/TreasuryPanel";
import { LinkagePanel } from "@/components/commons/LinkagePanel";
import { PilotProgramme } from "@/components/commons/PilotProgramme";
import { QualityPanel } from "@/components/commons/QualityPanel";
import { Assistant } from "@/components/Assistant";
import { Wheat, Loader2, Wallet } from "lucide-react";

const CITIES = ["Konya", "Antalya", "Malatya", "İstanbul", "Ankara", "İzmir", "Adana", "Gaziantep", "Hatay", "Kahramanmaraş", "Bursa", "Erzincan", "Van"];
const TABS = [["prices", "Price board"], ["lots", "Lots · farm to fork"], ["coops", "Cooperatives & collective"], ["schedules", "Production schedules"], ["audit", "Inspections & audit"], ["treasury", "Collective treasury"], ["linkage", "Farmer linkage"], ["policy", "Policy evolution"], ["pilot", "Pilot programme"], ["quality", "Quality QR"]];

export default function Commons() {
  const { user } = useAuth();
  const [city, setCity] = useState("Konya");
  const [tab, setTab] = useState("prices");
  const [d, setD] = useState(null);
  const [wallet, setWallet] = useState(null);

  const load = useCallback(async () => {
    const p = { params: { city } };
    const [prices, lots, coops, schedules, audit, anomalies] = await Promise.all([
      axios.get(`${API}/commons/prices`, p), axios.get(`${API}/commons/lots`, p), axios.get(`${API}/commons/coops`, p),
      axios.get(`${API}/commons/schedules`, p), axios.get(`${API}/commons/audit`, p), axios.get(`${API}/commons/anomalies`, p),
    ]);
    setD({ board: prices.data.board, lots: lots.data, coops: coops.data, schedules: schedules.data, audit: audit.data, anomalies: anomalies.data });
    if (user) api.get("/commons/wallet").then((r) => setWallet(r.data.try_balance)).catch(() => {});
  }, [city, user]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#050A15]">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 text-[#00FF7F] text-xs font-mono uppercase tracking-widest"><Wheat className="h-4 w-4" /> İmece Commons · farm to fork</span>
            <h1 className="font-display font-bold text-3xl md:text-5xl mt-2 tracking-tight" data-testid="commons-title">Collective production, fair market</h1>
            <p className="text-slate-400 mt-3 max-w-3xl">Cooperatives list produce with its hyperspectral provenance; anyone orders directly; every price, trade and inspection is anchored and audited — imece, for a free and resilient society.</p>
          </div>
          <div className="flex items-center gap-3">
            {wallet != null && <span className="flex items-center gap-1.5 text-xs font-mono border border-white/10 px-3 py-2" data-testid="commons-wallet"><Wallet className="h-3.5 w-3.5 text-[#00FFFF]" /> {wallet.toLocaleString()} TRY</span>}
            <select data-testid="commons-city" value={city} onChange={(e) => setCity(e.target.value)} className="bg-[#0A1128] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#00FF7F]">
              {CITIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 mt-8 border-b border-white/10" data-testid="commons-tabs">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm border-b-2 transition-colors ${tab === k ? "border-[#00FF7F] text-white" : "border-transparent text-slate-500 hover:text-slate-300"}`}>{l}</button>
          ))}
        </div>

        <div className="mt-6">
          {!d ? <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#00FF7F]" /></div> : (
            <>
              {tab === "prices" && <PriceBoard board={d.board} />}
              {tab === "lots" && <LotsMarket lots={d.lots} coops={d.coops} onChange={load} />}
              {tab === "coops" && <CoopsPanel city={city} coops={d.coops} onChange={load} />}
              {tab === "schedules" && <SchedulesPanel schedules={d.schedules} coops={d.coops} onChange={load} />}
              {tab === "audit" && <AuditPanel audit={d.audit} anomalies={d.anomalies} lots={d.lots} onChange={load} />}
              {tab === "treasury" && <TreasuryPanel city={city} key={`${city}-${d.audit.length}`} />}
              {tab === "linkage" && <LinkagePanel city={city} coops={d.coops} />}
              {tab === "policy" && <PolicyPanel city={city} />}
              {tab === "pilot" && <PilotProgramme />}
              {tab === "quality" && <QualityPanel />}
            </>
          )}
        </div>
      </div>
      <Footer />
      <Assistant page={`/commons#${tab}`} />
    </div>
  );
}
